/* eslint-disable @typescript-eslint/no-explicit-any */

interface DebounceSettings {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
}

export interface DebouncedFunc<T extends (...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: () => ReturnType<T> | undefined;
}

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait = 0,
  options: DebounceSettings = {},
): DebouncedFunc<T> => {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let lastCallTime: number | null = null;
  let lastInvokeTime = 0;
  let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastThis: ThisParameterType<T> | null = null;
  let result: ReturnType<T> | undefined;

  const leading = options.leading ?? false;
  const trailing = options.trailing !== false;
  const maxWait = options.maxWait;

  function invokeFunc(time: number) {
    lastInvokeTime = time;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    result = func.apply(lastThis, lastArgs!);
    lastArgs = null;
    lastThis = null;
    return result;
  }

  function startTimer(pendingFunc: () => void, wait: number) {
    if (timerId) clearTimeout(timerId);
    return setTimeout(pendingFunc, wait);
  }

  function shouldInvoke(time: number) {
    if (lastCallTime === null) return true;
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    return timeSinceLastCall >= wait || (maxWait !== undefined && timeSinceLastInvoke >= maxWait);
  }

  function trailingEdge(time: number) {
    timerId = null;

    if (trailing && lastArgs) {
      return invokeFunc(time);
    }

    lastArgs = null;
    lastThis = null;
    return result;
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timerId = startTimer(timerExpired, wait);
  }

  function debounced(this: ThisParameterType<T>, ...args: Parameters<T>) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === null) {
        if (leading) {
          return invokeFunc(time);
        }
        timerId = startTimer(timerExpired, wait);
      }

      if (maxWait !== undefined) {
        if (maxWaitTimer) clearTimeout(maxWaitTimer);
        maxWaitTimer = startTimer(() => {
          if (lastArgs) {
            invokeFunc(Date.now());
          }
        }, maxWait);
      }
    }

    if (timerId === null) {
      timerId = startTimer(timerExpired, wait);
    }

    return result;
  }

  debounced.cancel = () => {
    if (timerId) clearTimeout(timerId);
    if (maxWaitTimer) clearTimeout(maxWaitTimer);
    timerId = null;
    maxWaitTimer = null;
    lastArgs = null;
    lastThis = null;
    lastCallTime = null;
  };

  debounced.flush = () => {
    if (timerId && lastArgs) {
      return trailingEdge(Date.now());
    }
    return result;
  };

  return debounced;
};
